import { Box, FormControl, FormLabel, Input, Text } from '@chakra-ui/react';
import { useAppDispatch } from 'modules/Editor/store';
import { updateForm } from 'modules/Editor/store/interactionSlice';
import { MouseEvent, useRef, useState } from 'react';
import { WidgetState } from 'widgets/TextBasedWidgets/common/TextBasedWidgets.types';
import { useWidget } from 'widgets/sdk';

import { SHORT_QUESTION_INPUT_TEST_ID } from '../FormWidget.config';
import { FormProps, FormWidgetData } from '../FormWidget.types';

const ShortQuestion = ({
  isReadOnly = false,
  labelFocus = false,
  setWidgetState = (_: WidgetState) => {},
}: FormProps) => {
  const { widgetId, style, field: formField, labelPosition, isWidgetSelected } = useWidget<FormWidgetData>();
  const {
    gap,
    borderRadius,
    backgroundColor,
    borderColor,
    borderWidth,
    fontSize,
    alignSelf,
    textAlign,
    ...labelStyles
  } = style;
  const isHorizontal = labelPosition === 'left';
  const inputRef = useRef<HTMLInputElement>(null);
  const [value, setValue] = useState(formField.value);
  const dispatch = useAppDispatch();

  const handleInputClick = () => {
    inputRef.current?.focus();
    setWidgetState(WidgetState.Active);
  };

  const handleDoubleClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const handleLabelTextClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setWidgetState(WidgetState.Edit);
  };

  const handleLabelClick = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setWidgetState(WidgetState.Active);
  };

  const handleInputSubmit = () => {
    if (!isReadOnly) return;

    dispatch(
      updateForm({
        id: widgetId,
        fields: {
          label: formField.label,
          input: value,
        },
      }),
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  return (
    <Box id={`form-shortQuestion-${widgetId}`} padding='1'>
      <FormControl
        {...(isHorizontal
          ? { display: 'flex', alignItems: 'center', gap: `${gap}px` }
          : { display: 'flex', flexDirection: 'column', gap: `${gap}px` })}
      >
        {formField.label && (
          <FormLabel
            fontSize={`${fontSize}px`}
            style={{
              ...labelStyles,
              ...(isHorizontal ? { alignSelf } : { textAlign }),
            }}
            onClick={handleLabelClick}
            m={0}
            cursor='move'
            width={isHorizontal ? '50%' : '100%'}
          >
            <Text
              as='span'
              display='inline-block'
              maxWidth='100%'
              textDecoration='inherit'
              onClick={handleLabelTextClick}
              border={isWidgetSelected && labelFocus ? '1px solid var(--vg-colors-upgrade-blue-500)' : 'none'}
              cursor='pointer'
            >
              {formField.label}
            </Text>
          </FormLabel>
        )}
        <Input
          ref={inputRef}
          type={formField.type}
          data-testid={SHORT_QUESTION_INPUT_TEST_ID}
          value={value}
          style={{
            backgroundColor,
            borderRadius,
            borderColor,
            borderWidth,
          }}
          placeholder={formField.placeholder}
          onClick={handleInputClick}
          onDoubleClick={handleDoubleClick}
          onBlur={handleInputSubmit}
          onChange={handleInputChange}
          _hover={{
            borderColor: 'upgrade.blue.500',
          }}
          _focusVisible={{
            borderColor,
          }}
          _placeholder={{
            fontSize: `${fontSize}px`,
            fontFamily: labelStyles.fontFamily,
          }}
        />
      </FormControl>
    </Box>
  );
};
ShortQuestion.displayName = 'ShortQuestion';

export default ShortQuestion;
